import { mockDelay, mockHttpError } from './withMockFallback';

/**
 * Mock detection profile (Part 4, разделы 6.2 и 6.3). DTO повторяет ТЗ 1:1.
 *
 * Состояние живёт в модуле: в пределах сессии профиль ведёт себя как настоящий —
 * помнит manual_features, пересчитывает effective_enabled и поднимает версию.
 * Пересчёт делает «backend», а не UI: фронту здесь принадлежит только запрос.
 *
 * Список фич намеренно не совпадает с legacy-набором hats/gloves/masks: helmet
 * добавляется в данные и должен появиться в UI без правок кода.
 *
 * PATCH повторяет production-контракт: expected_version, manual_features,
 * disable_features, force; 409 приходит объектом detail с полем code.
 */

const buildFeature = (feature) => ({
  required_by_rule_items: [],
  available: true,
  ...feature,
});

const createProfile = (cameraId) => ({
  camera_id: cameraId,
  analytics_mode: 'people_ppe',
  version: 7,
  updated_at: '2026-09-07T12:00:00Z',
  features: [
    buildFeature({
      feature_id: 'people',
      display_name: 'Люди',
      category: 'base',
      manual_enabled: true,
      required_by_rule_items: [{ rule_id: 'r1', name: 'Люди в запрещённой зоне' }],
    }),
    buildFeature({
      feature_id: 'hats',
      display_name: 'Шапки',
      category: 'ppe',
      manual_enabled: false,
      required_by_rule_items: [{ rule_id: 'r2', name: 'Без головного убора' }],
    }),
    buildFeature({
      feature_id: 'gloves',
      display_name: 'Перчатки',
      category: 'ppe',
      manual_enabled: true,
    }),
    buildFeature({
      feature_id: 'masks',
      display_name: 'Маски',
      category: 'ppe',
      manual_enabled: false,
    }),
    buildFeature({
      feature_id: 'helmet',
      display_name: 'Каски',
      category: 'ppe',
      manual_enabled: false,
    }),
  ],
});

const store = new Map();

const getState = (cameraId) => {
  const key = String(cameraId);
  if (!store.has(key)) store.set(key, createProfile(Number(cameraId)));
  return store.get(key);
};

/** Пересчёт, который в проде делает backend: фронт его не повторяет. */
const decorate = (state) => ({
  ...state,
  features: state.features.map((feature) => {
    const requiredByRules = feature.required_by_rule_items.length > 0;
    return {
      ...feature,
      required_by_rules: requiredByRules,
      effective_enabled: feature.manual_enabled || requiredByRules,
    };
  }),
});

export const getDetectionProfileMock = (cameraId) =>
  mockDelay(decorate(getState(cameraId)));

export const updateDetectionProfileMock = (cameraId, payload = {}) => {
  const state = getState(cameraId);
  const {
    expected_version: expectedVersion,
    manual_features: manualFeatures = [],
    disable_features: disableFeatures = [],
    force,
  } = payload;

  if (expectedVersion !== state.version) {
    return Promise.reject(mockHttpError(409, {
      detail: {
        code: 'detection_profile_version_conflict',
        current_version: state.version,
      },
    }));
  }

  // Явно выключаемая фича, которую держат активные правила: detail — объект,
  // ровно как у production backend.
  const blocked = state.features.filter((feature) => (
    disableFeatures.includes(feature.feature_id) && feature.required_by_rule_items.length > 0
  ));

  if (blocked.length > 0 && !force) {
    return Promise.reject(mockHttpError(409, {
      detail: {
        code: 'detector_required_by_active_rules',
        features: blocked.map((feature) => feature.feature_id),
        dependent_rules: blocked.flatMap((feature) => feature.required_by_rule_items),
        current_version: state.version,
      },
    }));
  }

  state.features = state.features.map((feature) => {
    const manual = manualFeatures.includes(feature.feature_id);
    const disabled = disableFeatures.includes(feature.feature_id);

    // force=true: правила, державшие детектор, отвязываются на стороне backend.
    if (disabled && force) {
      return { ...feature, manual_enabled: false, required_by_rule_items: [] };
    }
    return { ...feature, manual_enabled: manual };
  });

  state.version += 1;
  state.updated_at = new Date().toISOString();

  // Production PATCH отдаёт сам профиль: convenience-поля deactivated_rules нет,
  // состояние правил фронт перечитывает отдельным запросом.
  return mockDelay(decorate(state));
};

/** Только для тестов: вернуть моки в исходное состояние. */
export const resetDetectionProfileMock = () => store.clear();
